import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UPLOAD_STAGES } from "@/lib/reportVaultConstants";

// Owns the multi-file "AI Report Vault" upload queue for the Patient Profile
// page — selecting files, sequential background upload + AI-analysis polling
// per file (with a simulated stage progress ticker), and viewing a stored file.
export function useReportUploadQueue({ patientId, onComplete }) {
  const [uploadCategory, setUploadCategory] = useState("lab_report");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadQueue, setUploadQueue] = useState([]);

  const handleFilesSelected = (e) => {
    setPendingFiles(Array.from(e.target.files || []));
  };

  const updateQueueItem = (localId, patch) => {
    setUploadQueue((q) => q.map((it) => (it.localId === localId ? { ...it, ...patch } : it)));
  };

  const pollQueueItemAnalysis = (localId, reportId) => {
    return new Promise((resolve) => {
      let stageIndex = 1;
      updateQueueItem(localId, { status: "active", stageIndex });
      const stageTimer = setInterval(() => {
        stageIndex = Math.min(stageIndex + 1, UPLOAD_STAGES.length - 2);
        updateQueueItem(localId, { stageIndex });
      }, 3500);

      let attempt = 0;
      const poll = async () => {
        try {
          const { data } = await api.get(`/ai/reports/${reportId}/analysis`);
          if (data) {
            clearInterval(stageTimer);
            updateQueueItem(localId, { status: "completed", stageIndex: UPLOAD_STAGES.length - 1 });
            resolve();
            return;
          }
        } catch (pollError) {
          console.debug("Report analysis not ready yet, retrying:", pollError);
        }
        attempt += 1;
        if (attempt >= 12) {
          clearInterval(stageTimer);
          updateQueueItem(localId, { status: "completed", stageIndex: UPLOAD_STAGES.length - 1 });
          resolve();
          return;
        }
        setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    });
  };

  const uploadOne = async (item) => {
    updateQueueItem(item.localId, { status: "active", stageIndex: 0 });
    const fd = new FormData();
    fd.append("category", item.category);
    fd.append("file", item.file);
    try {
      const { data: uploaded } = await api.post(`/reports/${patientId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateQueueItem(item.localId, { reportId: uploaded.id });
      if (["lab_report", "previous_report"].includes(item.category)) {
        await pollQueueItemAnalysis(item.localId, uploaded.id);
      } else {
        updateQueueItem(item.localId, { status: "completed", stageIndex: UPLOAD_STAGES.length - 1 });
      }
      toast.success(`${item.fileName} processed successfully`);
    } catch (e) {
      updateQueueItem(item.localId, { status: "failed", error: formatApiError(e.response?.data?.detail) || "Upload failed" });
      toast.error(`Failed to process ${item.fileName}`);
    }
  };

  const processQueue = async (items) => {
    for (const item of items) {
      await uploadOne(item);
    }
    onComplete();
  };

  const startUpload = () => {
    if (pendingFiles.length === 0) return;
    const items = pendingFiles.map((file, i) => ({
      localId: `${Date.now()}-${i}`,
      fileName: file.name,
      file,
      category: uploadCategory,
      status: "queued",
      stageIndex: 0,
      error: null,
      reportId: null,
    }));
    setUploadQueue((q) => [...q, ...items]);
    setPendingFiles([]);
    processQueue(items);
  };

  const dismissQueue = () => setUploadQueue((q) => q.filter((it) => it.status === "active"));
  const isUploading = uploadQueue.some((it) => it.status === "active" || it.status === "queued");

  const viewReport = async (reportId) => {
    try {
      const res = await api.get(`/reports/download/${reportId}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      window.open(blobUrl, "_blank");
    } catch (e) {
      let message = "Unable to open report";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          message = formatApiError(JSON.parse(text).detail) || message;
        } catch (parseError) {
          console.debug("Could not parse error blob, using default message:", parseError);
        }
      }
      toast.error(message);
    }
  };

  return {
    uploadCategory, setUploadCategory,
    pendingFiles, handleFilesSelected,
    uploadQueue, isUploading, startUpload, dismissQueue,
    viewReport,
  };
}
